/**
 * Seeded PRNG (mulberry32). Small, fast, and identical on every platform, which
 * is what the replay contract needs. v0.1 levels use scripted spawns and do not
 * draw from this, but bots and future traffic generators do.
 */

/** Advance the state and return a float in [0, 1). */
export function nextFloat(state: number): { state: number; value: number } {
  let t = (state + 0x6d2b79f5) | 0;
  t = Math.imul(t ^ (t >>> 15), 1 | t);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return { state: t | 0, value };
}

/** Integer in [min, max] inclusive. */
export function nextInt(state: number, min: number, max: number): { state: number; value: number } {
  const r = nextFloat(state);
  return { state: r.state, value: min + Math.floor(r.value * (max - min + 1)) };
}

/** Convenience wrapper for call sites that want a stateful generator. */
export class Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed | 0;
  }

  float(): number {
    const r = nextFloat(this.state);
    this.state = r.state;
    return r.value;
  }

  int(min: number, max: number): number {
    const r = nextInt(this.state, min, max);
    this.state = r.state;
    return r.value;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick: empty array');
    return items[this.int(0, items.length - 1)] as T;
  }
}
